/**
 * Job profiles API client — perfis de vaga (migration 0022).
 *
 * Espelha `lib/api/opportunities.ts`: helpers finos sobre
 * `apps/api/src/routes/job-profiles.ts`, com o cookie de sessão
 * (`credentials: 'include'`) e leitura de erro em RFC 7807 pelo
 * `readProblemMessage` partilhado.
 *
 * Um perfil é o CARGO-TIPO ("Desenvolvedor C#"): nome, slug, descrição e
 * estado. Não carrega avaliações — a 0022 punha aqui dois modelos "por
 * omissão" que a vaga copiava, e a 0027 tirou-os a pedido do owner. Quem
 * escolhe entrevista e prova é a vaga, em `lib/api/opportunities.ts`.
 *
 * A exclusão é a única operação com corpo estruturado: o servidor recusa com
 * 409 quando há vagas vinculadas, e a interface precisa do NÚMERO para
 * explicar o bloqueio em vez de mostrar uma frase genérica. Daí a classe
 * tipada abaixo, na mesma disciplina de `lib/api/professional-errors.ts`.
 */
import { API_BASE } from '@/lib/api'
import {
  parseProblemJson,
  readProblemExtension,
  readProblemMessage,
  toExistingRecord,
  type ExistingRecord,
} from '@/lib/api/problem'

export type JobProfileStatus = 'active' | 'inactive'

export interface JobProfile {
  id: string
  slug: string
  name: string
  description: string | null
  status: JobProfileStatus
  /** ISO-8601 (Hono `c.json` serializa as colunas de data). */
  createdAt: string
  updatedAt: string
  /** Quantas vagas usam este perfil — é o que trava a exclusão (409). */
  opportunityCount: number
}

export interface JobProfilesListResponse {
  items: JobProfile[]
  page: number
  pageSize: number
  total: number
}

export interface CreateJobProfileInput {
  name: string
  slug?: string
  description?: string | null
  status?: JobProfileStatus
}

export type UpdateJobProfileInput = Partial<CreateJobProfileInput>

/**
 * 409 do DELETE: o perfil tem vagas vinculadas.
 *
 * Carrega a contagem porque a interface precisa dela para dizer QUANTAS vagas
 * bloqueiam — um `Error` genérico com o texto do servidor deixaria o diálogo
 * sem como montar a frase, e sem como decidir o plural.
 */
export class JobProfileLinkedError extends Error {
  readonly opportunities: number

  constructor(init: { message: string; opportunities: number }) {
    super(init.message)
    this.name = 'JobProfileLinkedError'
    this.opportunities = init.opportunities
  }
}

/**
 * 409 do POST/PATCH: o slug já é de outro perfil (Story 36.3).
 *
 * Carrega o dono (`existing`) quando o servidor o diz: o cadastro rápido da vaga
 * oferece «Usar este» a um ativo, e diz onde reativar um inativo. A mensagem é a
 * frase do servidor, a mesma que a tela de perfis já mostrava num `Error`.
 */
export class DuplicateJobProfileError extends Error {
  readonly existing: ExistingRecord | null

  constructor(init: { message: string; existing?: ExistingRecord | null }) {
    super(init.message)
    this.name = 'DuplicateJobProfileError'
    this.existing = init.existing ?? null
  }
}

const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' }

const acceptJson = { Accept: 'application/json' }

/**
 * O erro de uma escrita (POST/PATCH) que falhou. O corpo lê-se UMA vez: no 409
 * precisa-se da frase e do `existing`, e o `readProblemMessage` consumiria o
 * stream antes do segundo.
 */
async function writeError(res: Response, fallback: string): Promise<Error> {
  if (res.status !== 409)
    return new Error(await readProblemMessage(res, fallback, ['detail', 'title']))
  const body = await parseProblemJson(res)
  const message =
    (typeof body?.detail === 'string' && body.detail) ||
    (typeof body?.title === 'string' && body.title) ||
    fallback
  const existing = body ? toExistingRecord(readProblemExtension(body, 'existing')) : null
  return new DuplicateJobProfileError({ message, existing })
}

/**
 * Lê a contagem de vagas do corpo do 409.
 *
 * O contrato põe os membros de extensão dentro de `extensions`, mas o RFC 7807
 * também os admite na raiz do documento — e o resto desta app (ver
 * `professional-errors.ts`) lê-os da raiz. Procurar nos dois sítios custa uma
 * linha e evita que a UI caia no caminho genérico por causa da forma do corpo.
 */
function readLinkedCount(body: Record<string, unknown>): number | null {
  const extensions = body.extensions
  if (typeof extensions === 'object' && extensions !== null) {
    const nested = (extensions as Record<string, unknown>).opportunities
    if (typeof nested === 'number') return nested
  }
  return typeof body.opportunities === 'number' ? body.opportunities : null
}

/** GET /api/job-profiles — lista paginada, filtro de estado opcional. */
export async function listJobProfiles(
  params: {
    status?: JobProfileStatus
    page?: number
    pageSize?: number
    signal?: AbortSignal
  } = {},
): Promise<JobProfilesListResponse> {
  const url = new URL(`${API_BASE}/job-profiles`, window.location.origin)
  if (params.status) url.searchParams.set('status', params.status)
  url.searchParams.set('page', String(params.page ?? 1))
  url.searchParams.set('pageSize', String(params.pageSize ?? 50))

  const init: RequestInit = { credentials: 'include', headers: acceptJson }
  if (params.signal) init.signal = params.signal

  const res = await fetch(`${url.pathname}${url.search}`, init)
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Não foi possível carregar os perfis (${res.status})`, [
        'detail',
        'title',
      ]),
    )
  }
  return (await res.json()) as JobProfilesListResponse
}

/** GET /api/job-profiles/:id. */
export async function getJobProfile(id: string, signal?: AbortSignal): Promise<JobProfile> {
  const init: RequestInit = { credentials: 'include', headers: acceptJson }
  if (signal) init.signal = signal
  const res = await fetch(`${API_BASE}/job-profiles/${encodeURIComponent(id)}`, init)
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Não foi possível carregar o perfil (${res.status})`, [
        'detail',
        'title',
      ]),
    )
  }
  return (await res.json()) as JobProfile
}

/** POST /api/job-profiles. 409 = slug em uso (`DuplicateJobProfileError`). */
export async function createJobProfile(body: CreateJobProfileInput): Promise<JobProfile> {
  const res = await fetch(`${API_BASE}/job-profiles`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  if (!res.ok) throw await writeError(res, `Não foi possível criar o perfil (${res.status})`)
  return (await res.json()) as JobProfile
}

/** PATCH /api/job-profiles/:id. 409 = slug em uso (`DuplicateJobProfileError`). */
export async function updateJobProfile(
  id: string,
  body: UpdateJobProfileInput,
): Promise<JobProfile> {
  const res = await fetch(`${API_BASE}/job-profiles/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  if (!res.ok) throw await writeError(res, `Não foi possível salvar o perfil (${res.status})`)
  return (await res.json()) as JobProfile
}

/**
 * DELETE /api/job-profiles/:id — exclusão definitiva.
 *
 * 409 com vagas vinculadas vira `JobProfileLinkedError` (com a contagem);
 * qualquer outro erro vira `Error` com a frase do servidor. Não devolve nada:
 * o corpo do sucesso não é contrato, e ler um 204 como JSON lançaria.
 */
export async function deleteJobProfile(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/job-profiles/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: acceptJson,
  })
  if (res.ok) return

  const fallback = `Não foi possível excluir o perfil (${res.status})`
  if (res.status === 409) {
    // O corpo é lido UMA vez — daí o parse cru aqui em vez de
    // `readProblemMessage`, que consumiria o stream antes da contagem.
    const body = await parseProblemJson(res)
    const message =
      (typeof body?.detail === 'string' && body.detail) ||
      (typeof body?.title === 'string' && body.title) ||
      fallback
    const opportunities = body ? readLinkedCount(body) : null
    if (opportunities !== null) throw new JobProfileLinkedError({ message, opportunities })
    throw new Error(message)
  }

  throw new Error(await readProblemMessage(res, fallback, ['detail', 'title']))
}
